import React from "react";
import { FaReceipt, FaTruck, FaTag, FaMapMarkerAlt } from "react-icons/fa";

const fmt = (n) => `₹${Number(n || 0).toFixed(2)}`;

const CartBillingSummary = ({
  subtotal = 0,
  taxAmount = 0,
  deliveryFee = 0,
  discountAmount = 0,
  appliedCoupon = null,
  totalAmount = 0,
  freeDeliveryMinAmount = 0,
  amountNeededForFreeDelivery = 0,
  appliedLocation = null,
  appliedPincode = null,
  totalItems = 0,
  totalQuantity = 0,
}) => {
  const effectiveTotalAmount = totalAmount || subtotal + taxAmount + deliveryFee;

  // Free delivery progress
  const freeDeliveryThreshold = freeDeliveryMinAmount > 0 ? freeDeliveryMinAmount : 0;
  const amountNeeded = amountNeededForFreeDelivery > 0 ? amountNeededForFreeDelivery : 0;
  const showFreeDeliveryProgress = freeDeliveryThreshold > 0 && deliveryFee > 0;
  const progressPct = showFreeDeliveryProgress
    ? Math.min(100, ((freeDeliveryThreshold - amountNeeded) / freeDeliveryThreshold) * 100)
    : 0;

  // Total savings (item discounts)
  const totalSavings = discountAmount;

  // Location display
  const locationStr = appliedLocation
    ? [appliedLocation.area_name, appliedLocation.city_name, appliedLocation.state]
        .filter(Boolean)
        .join(", ")
    : appliedPincode || null;

  return (
    <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100 bg-gray-50/60">
        <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-emerald-50">
          <FaReceipt className="h-4 w-4 text-[#046a38]" />
        </div>
        <h2 className="text-base font-bold text-gray-900">Billing Summary</h2>
        {(totalItems > 0 || totalQuantity > 0) && (
          <span className="ml-auto text-xs text-gray-400 font-medium">
            {totalItems > 0 ? `${totalItems} product${totalItems !== 1 ? "s" : ""}` : ""}
            {totalQuantity > 0 ? ` · ${totalQuantity} qty` : ""}
          </span>
        )}
      </div>

      <div className="p-4 space-y-2.5 text-sm text-gray-700">
        {/* Subtotal */}
        <div className="flex justify-between items-center">
          <span className="text-gray-600">Subtotal (MRP)</span>
          <span className="font-semibold text-gray-900">{fmt(subtotal)}</span>
        </div>

        {/* Coupon / Discount savings */}
        {totalSavings > 0 && (
          <div className="flex justify-between items-center font-semibold text-emerald-700">
            <span className="flex items-center gap-1.5">
              <FaTag className="h-3 w-3" />
              {appliedCoupon?.coupon_code
                ? `Coupon (${appliedCoupon.coupon_code})`
                : "Discount Savings"}
            </span>
            <span>− {fmt(totalSavings)}</span>
          </div>
        )}

        {/* GST / Tax */}
        <div className="flex justify-between items-center">
          <span className="text-gray-600">GST / Taxes</span>
          <span className="font-semibold text-gray-900">
            {taxAmount > 0 ? `+ ${fmt(taxAmount)}` : fmt(taxAmount)}
          </span>
        </div>

        {/* Delivery Fee */}
        <div className="flex justify-between items-center">
          <span className="flex items-center gap-1.5 text-gray-600">
            <FaTruck className="h-3 w-3 text-gray-400" />
            Delivery Fee
          </span>
          <span className={`font-semibold ${deliveryFee === 0 ? "text-emerald-600" : "text-gray-900"}`}>
            {deliveryFee === 0 ? "FREE" : `+ ${fmt(deliveryFee)}`}
          </span>
        </div>

        {/* Divider */}
        <div className="border-t border-dashed border-gray-200 pt-2.5 mt-1" />

        {/* Total row */}
        <div className="flex justify-between items-center">
          <p className="text-sm font-bold text-gray-900">Total Payable</p>
          <div className="text-right">
            <p className="text-lg font-black text-[#046a38]">{fmt(effectiveTotalAmount)}</p>
          </div>
        </div>

        {/* Total savings callout */}
        {totalSavings > 0 && (
          <div className="flex items-center justify-between rounded-lg bg-emerald-50 border border-emerald-100 px-3 py-2 mt-1">
            <span className="text-xs font-semibold text-emerald-700">🎉 Your total savings</span>
            <span className="text-xs font-black text-emerald-700">{fmt(totalSavings)}</span>
          </div>
        )}

        {/* Free delivery progress bar */}
        {showFreeDeliveryProgress && (
          <div className="mt-1 rounded-lg border border-blue-100 bg-blue-50/60 px-3 py-2.5 space-y-1.5">
            <div className="flex items-center justify-between text-xs">
              <span className="flex items-center gap-1 font-semibold text-blue-700">
                <FaTruck className="h-3 w-3" />
                Free Delivery
              </span>
              {amountNeeded > 0 ? (
                <span className="text-blue-600 font-medium">
                  Add {fmt(amountNeeded)} more
                </span>
              ) : (
                <span className="text-emerald-600 font-bold">✓ Unlocked!</span>
              )}
            </div>
            <div className="h-1.5 w-full rounded-full bg-blue-100 overflow-hidden">
              <div
                className="h-full rounded-full bg-gradient-to-r from-blue-400 to-emerald-500 transition-all duration-500"
                style={{ width: `${progressPct}%` }}
              />
            </div>
            <p className="text-[10px] text-blue-500">
              Free delivery on orders above {fmt(freeDeliveryThreshold)}
            </p>
          </div>
        )}

        {/* Applied location */}
        {locationStr && (
          <div className="flex items-center gap-2 rounded-lg bg-gray-50 border border-gray-100 px-3 py-2 text-xs text-gray-500 mt-1">
            <FaMapMarkerAlt className="h-3 w-3 text-emerald-500 shrink-0" />
            <span>
              Delivering to:{" "}
              <span className="font-semibold text-gray-700">{locationStr}</span>
              {appliedPincode ? ` - ${appliedPincode}` : ""}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default CartBillingSummary;
